import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const API_BASE = "/api/ebay-intelligence";
const DEFAULT_INTERVAL_MINUTES = 1440;
const FILTER_OPTIONS = [
  { value: "stale_listed", label: "Stale listed" },
  { value: "failed", label: "Failed syncs" },
  { value: "listed", label: "All listed" },
  { value: "oldest", label: "Oldest sync" },
];

const dateText = (value) => {
  if (!value) return "Not scheduled";
  try {
    const normalised = typeof value === "string" ? value.replace(" ", "T") : value;
    const date = new Date(normalised);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch (e) {
    return String(value);
  }
};

const numberText = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : "0";
};

const buildDraft = (state = {}) => ({
  enabled: Boolean(state.enabled),
  interval_minutes: state.interval_minutes || DEFAULT_INTERVAL_MINUTES,
  batch_limit: state.batch_limit || 25,
  filter_mode: state.filter_mode || "stale_listed",
  max_consecutive_failures: state.max_consecutive_failures || 3,
});

const resultSummary = (result) => {
  const sync = result?.sync_result || result;
  const parts = [];
  if (sync?.attempted !== undefined) parts.push(`attempted ${numberText(sync.attempted)}`);
  if (sync?.refreshed !== undefined) parts.push(`refreshed ${numberText(sync.refreshed)}`);
  if (sync?.failed !== undefined) parts.push(`failed ${numberText(sync.failed)}`);
  if (sync?.skipped) parts.push(sync.reason || "skipped");
  return parts.length ? parts.join(" / ") : "No run detail returned";
};

const historySummary = (row = {}) => {
  if (row.error) return row.error;
  if (row.reason) return row.reason;
  const parts = [];
  if (row.attempted !== null && row.attempted !== undefined) parts.push(`attempted ${numberText(row.attempted)}`);
  if (row.refreshed !== null && row.refreshed !== undefined) parts.push(`refreshed ${numberText(row.refreshed)}`);
  if (row.failed !== null && row.failed !== undefined) parts.push(`failed ${numberText(row.failed)}`);
  if (row.duration_ms !== null && row.duration_ms !== undefined) parts.push(`${numberText(row.duration_ms)}ms`);
  return parts.length ? parts.join(" / ") : "No detail";
};

const statusChipColor = (status) => {
  if (status === "success") return "success";
  if (status === "failure") return "error";
  if (status === "running") return "info";
  if (status === "skipped") return "warning";
  return "default";
};

export default function EbayListingSyncAutomationPanel({ onMessage, onError, onRefresh }) {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(buildDraft());
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [localMessage, setLocalMessage] = useState(null);

  const statusColor = useMemo(() => {
    if (!state?.schema_ready) return "warning";
    if (state?.state?.status === "running") return "info";
    if (state?.state?.enabled) return "success";
    return "default";
  }, [state]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/listing-sync-automation/history?limit=10`);
      const json = await res.json();
      if (!res.ok && json.schema_ready !== false) {
        throw new Error(json.error || json.message || "Failed to load listing sync automation history");
      }
      setHistory(Array.isArray(json.history) ? json.history : []);
    } catch (e) {
      const message = e.message || String(e);
      setHistory([]);
      if (onError) onError(message);
    }
  }, [onError]);

  const loadAutomationState = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/listing-sync-automation`);
      const json = await res.json();
      if (!res.ok && json.schema_ready !== false) {
        throw new Error(json.error || json.message || "Failed to load listing sync automation");
      }
      setState(json);
      setDraft(buildDraft(json.state));
      if (json.schema_ready === false) {
        setHistory([]);
      } else {
        await loadHistory();
      }
    } catch (e) {
      const message = e.message || String(e);
      setError(message);
      if (onError) onError(message);
    } finally {
      setLoading(false);
    }
  }, [loadHistory, onError]);

  useEffect(() => {
    loadAutomationState();
  }, [loadAutomationState]);

  const saveConfig = async () => {
    setSaving(true);
    setError(null);
    setLocalMessage(null);
    try {
      const res = await fetch(`${API_BASE}/listing-sync-automation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || json.message || "Failed to save automation settings");
      }
      setState(json);
      setDraft(buildDraft(json.state));
      await loadHistory();
      const message = json.message || "Listing sync automation settings saved.";
      setLocalMessage(message);
      if (onMessage) onMessage(message);
    } catch (e) {
      const message = e.message || String(e);
      setError(message);
      if (onError) onError(message);
    } finally {
      setSaving(false);
    }
  };

  const runAutomation = async (path, successPrefix) => {
    setRunning(true);
    setError(null);
    setLocalMessage(null);
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: draft.batch_limit, filter_mode: draft.filter_mode }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || json.message || "Listing sync automation run failed");
      }
      setState((current) => ({ ...(current || {}), ok: true, schema_ready: true, state: json.state || current?.state }));
      const message = `${successPrefix}: ${resultSummary(json)}`;
      setLocalMessage(message);
      if (onMessage) onMessage(message);
      if (onRefresh) onRefresh();
      await loadAutomationState();
    } catch (e) {
      const message = e.message || String(e);
      setError(message);
      if (onError) onError(message);
      await loadAutomationState();
    } finally {
      setRunning(false);
    }
  };

  const automationState = state?.state || {};
  const schemaMissing = state && state.schema_ready === false;
  const busy = loading || saving || running;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
      {(loading || saving || running) && <LinearProgress sx={{ mb: 2 }} />}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-start" }} justifyContent="space-between">
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Listing sync automation
            </Typography>
            <Chip size="small" color={statusColor} label={automationState.status || (schemaMissing ? "Schema required" : "idle")} />
            {automationState.is_due && <Chip size="small" color="warning" label="Due now" />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Controlled eBay to app listing sync cadence for stale, failed, or listed inventory rows.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} sx={{ flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={loadAutomationState} disabled={busy}>
            Refresh
          </Button>
          <Button variant="contained" onClick={() => runAutomation("/listing-sync-automation/run", "Automation run complete")} disabled={busy || schemaMissing}>
            Run Now
          </Button>
          <Button variant="outlined" onClick={() => runAutomation("/listing-sync-automation/tick", "Automation tick complete")} disabled={busy || schemaMissing}>
            Run Due Tick
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mt: 2 }}>
        This automation is eBay → app only. It refreshes app listing data from eBay and does not push price, quantity, title, or listing changes to eBay.
      </Alert>
      {schemaMissing && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {state.message || "Manual database install is required before automation can be used."}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {localMessage && <Alert severity="success" sx={{ mt: 2 }}>{localMessage}</Alert>}

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={<Switch checked={draft.enabled} onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))} disabled={busy || schemaMissing} />}
            label={draft.enabled ? "Enabled" : "Disabled"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Interval minutes"
            value={draft.interval_minutes}
            inputProps={{ min: 5, max: 1440 }}
            onChange={(event) => setDraft((current) => ({ ...current, interval_minutes: event.target.value }))}
            disabled={busy || schemaMissing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Batch limit"
            value={draft.batch_limit}
            inputProps={{ min: 1, max: 100 }}
            onChange={(event) => setDraft((current) => ({ ...current, batch_limit: event.target.value }))}
            disabled={busy || schemaMissing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small" disabled={busy || schemaMissing}>
            <InputLabel id="listing-sync-automation-filter-label">Filter</InputLabel>
            <Select
              labelId="listing-sync-automation-filter-label"
              value={draft.filter_mode}
              label="Filter"
              onChange={(event) => setDraft((current) => ({ ...current, filter_mode: event.target.value }))}
            >
              {FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Max failures"
            value={draft.max_consecutive_failures}
            inputProps={{ min: 1, max: 20 }}
            onChange={(event) => setDraft((current) => ({ ...current, max_consecutive_failures: event.target.value }))}
            disabled={busy || schemaMissing}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ height: "100%" }} alignItems={{ xs: "stretch", sm: "center" }}>
            <Chip label={`Next run: ${dateText(automationState.next_run_at)}`} />
            <Chip label={`Last success: ${dateText(automationState.last_success_at)}`} />
            <Chip label={`Failures: ${numberText(automationState.consecutive_failures)}`} color={automationState.consecutive_failures ? "warning" : "default"} />
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="contained" onClick={saveConfig} disabled={busy || schemaMissing}>
          Save Automation Settings
        </Button>
      </Stack>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
          Recent automation history
        </Typography>
        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No recent automation runs recorded yet.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Started</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Filter</TableCell>
                  <TableCell align="right">Limit</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id || `${row.started_at}-${row.status}`} hover>
                    <TableCell>{dateText(row.started_at)}</TableCell>
                    <TableCell>{row.trigger_type || "-"}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusChipColor(row.status)} label={row.status || "unknown"} />
                    </TableCell>
                    <TableCell>{row.filter_mode || "-"}</TableCell>
                    <TableCell align="right">{numberText(row.batch_limit)}</TableCell>
                    <TableCell>{historySummary(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
}
